import type { MedplumClient } from '@medplum/core';
import { config } from '../config';
import { resolveOrganizationIdFromEpisode } from './resolveEpisodeOrganization';
import type { EpisodeParty } from '../pages/clients/data/types';
import { manageEocParty, type ManageEocPartyBotInput } from './adminActionsService';

export function parseEpisodePartyId(partyId: string): {
  kind: 'patient' | 'relatedperson' | 'contact';
  patientId?: string;
  relatedPersonId?: string;
} {
  if (partyId.startsWith('relatedperson-')) {
    return { kind: 'relatedperson', relatedPersonId: partyId.replace('relatedperson-', '') };
  }
  if (partyId.startsWith('contact-')) {
    return { kind: 'contact' };
  }
  return { kind: 'patient', patientId: partyId };
}

export function partyToGuarantorReference(party: EpisodeParty): string | undefined {
  const parsed = parseEpisodePartyId(party.id);
  if (parsed.kind === 'patient' && parsed.patientId) {
    return `Patient/${parsed.patientId}`;
  }
  if (parsed.kind === 'relatedperson' && parsed.relatedPersonId) {
    return `RelatedPerson/${parsed.relatedPersonId}`;
  }
  return undefined;
}

async function resolveOrgId(
  medplum: MedplumClient,
  primaryPatientId: string,
  episodeId: string,
): Promise<string> {
  return resolveOrganizationIdFromEpisode(medplum, primaryPatientId, episodeId);
}

function baseBotInput(
  episodeId: string,
  primaryPatientId: string,
  orgId: string,
): Pick<ManageEocPartyBotInput, 'action' | 'episodeId' | 'primaryPatientId' | 'orgId' | 'projectId'> {
  return {
    action: 'manage-eoc-party',
    episodeId,
    primaryPatientId,
    orgId,
    projectId: config.medplum.projectId,
  };
}

export async function addExistingClientToEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    participantPatientId: string;
    relationship?: string;
  },
): Promise<void> {
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  await manageEocParty(medplum, {
    ...baseBotInput(opts.episodeId, opts.primaryPatientId, orgId),
    operation: 'add-participant-existing',
    participantPatientId: opts.participantPatientId,
    relationship: opts.relationship,
  });
}

export async function addNewClientToEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    firstName: string;
    lastName: string;
    email: string;
    relationship?: string;
  },
): Promise<{ patientId: string }> {
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  const result = await manageEocParty(medplum, {
    ...baseBotInput(opts.episodeId, opts.primaryPatientId, orgId),
    operation: 'add-participant-new',
    firstName: opts.firstName,
    lastName: opts.lastName,
    email: opts.email,
    relationship: opts.relationship,
  });
  if (!result.patientId) {
    throw new Error('Client was created but no patient id was returned.');
  }
  return { patientId: result.patientId };
}

export async function removeParticipantFromEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    member: EpisodeParty;
  },
): Promise<void> {
  const parsed = parseEpisodePartyId(opts.member.id);
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  const base = baseBotInput(opts.episodeId, opts.primaryPatientId, orgId);

  if (parsed.kind === 'patient' && parsed.patientId) {
    await manageEocParty(medplum, {
      ...base,
      operation: 'remove-participant',
      participantPatientId: parsed.patientId,
    });
    return;
  }

  const relatedPersonId = parsed.relatedPersonId ?? opts.member.relatedPersonId;
  if (!relatedPersonId) {
    throw new Error('Only linked participants can be removed from the episode.');
  }
  await manageEocParty(medplum, {
    ...base,
    operation: 'remove-participant',
    relatedPersonId,
  });
}

export async function setEpisodeGuarantor(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    guarantor: EpisodeParty;
    relationship?: string;
    sendInviteEmail?: boolean;
  },
): Promise<void> {
  const parsed = parseEpisodePartyId(opts.guarantor.id);
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  const base = baseBotInput(opts.episodeId, opts.primaryPatientId, orgId);

  if (parsed.kind === 'relatedperson' && parsed.relatedPersonId) {
    await manageEocParty(medplum, {
      ...base,
      operation: 'set-guarantor',
      guarantorPartyReference: `RelatedPerson/${parsed.relatedPersonId}`,
    });
    return;
  }

  if (parsed.kind === 'patient' && parsed.patientId) {
    if (parsed.patientId === opts.primaryPatientId) {
      await manageEocParty(medplum, {
        ...base,
        operation: 'set-guarantor',
        guarantorPartyReference: `Patient/${parsed.patientId}`,
      });
      return;
    }
    await manageEocParty(medplum, {
      ...base,
      operation: 'assign-guarantor-existing',
      guarantorPatientId: parsed.patientId,
      relationship: opts.relationship ?? opts.guarantor.relationship,
      sendInviteEmail: opts.sendInviteEmail,
    });
  }
}

export async function assignGuarantorExistingToEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    guarantorPatientId: string;
    relationship?: string;
    sendInviteEmail?: boolean;
  },
): Promise<void> {
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  await manageEocParty(medplum, {
    ...baseBotInput(opts.episodeId, opts.primaryPatientId, orgId),
    operation: 'assign-guarantor-existing',
    guarantorPatientId: opts.guarantorPatientId,
    relationship: opts.relationship,
    sendInviteEmail: opts.sendInviteEmail,
  });
}

export async function addGuarantorContactToEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    contactName: string;
    email: string;
    relationship?: string;
    phone?: string;
  },
): Promise<{ relatedPersonId: string; guarantorPartyReference: string }> {
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  const result = await manageEocParty(medplum, {
    ...baseBotInput(opts.episodeId, opts.primaryPatientId, orgId),
    operation: 'add-guarantor-contact',
    contactName: opts.contactName,
    email: opts.email,
    relationship: opts.relationship,
    phone: opts.phone,
  });
  if (!result.relatedPersonId || !result.guarantorPartyReference) {
    throw new Error('Guarantor contact was created but references were not returned.');
  }
  return {
    relatedPersonId: result.relatedPersonId,
    guarantorPartyReference: result.guarantorPartyReference,
  };
}

/**
 * Points the episode's `account[0]` at the keyed (patient, payer) Account,
 * creating the Account if needed. Defaults to the patient as their own payer
 * (self-pay). Runs server-side via the admin-actions bot so the account
 * keying rule has a single implementation.
 */
export async function linkEpisodeBillingAccount(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    guarantorPartyReference?: string;
  },
): Promise<void> {
  await manageEocParty(medplum, {
    operation: 'link-billing-account',
    episodeId: opts.episodeId,
    primaryPatientId: opts.primaryPatientId,
    guarantorPartyReference: opts.guarantorPartyReference,
    projectId: config.medplum.projectId,
  });
}

export function episodePartyFromGuarantorReference(
  ref: string,
  displayName: string,
  relationship?: string,
): EpisodeParty {
  if (ref.startsWith('Patient/')) {
    return { id: ref.replace('Patient/', ''), name: displayName, relationship: relationship ?? 'Self' };
  }
  if (ref.startsWith('RelatedPerson/')) {
    return {
      id: `relatedperson-${ref.replace('RelatedPerson/', '')}`,
      name: displayName,
      relationship: relationship ?? 'Guarantor',
    };
  }
  throw new Error(`Unsupported guarantor reference: ${ref}`);
}
